import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { PencilIcon, UserRoundIcon, XIcon } from 'lucide-react';
import { useActivitySensor } from '@archipro-website/react-hooks';
import { cn } from '@rocco/utils/cn';
import { Badge } from '@rocco/components/badge';
import { Separator } from '@rocco/components/separator';
import type { MeUser } from '~/server/types';
import { getUserOccupation } from '~/modules/user';
import type { PartialConversation } from '../../types';
import {
    CHAT_BOX_CLOSED_WIDTH_PX,
    CHAT_BOX_OPEN_WIDTH_PX,
    useChat,
} from '../../hooks/use-chat';
import { useConversation } from '../../hooks/use-conversation';
import { useRealTimeChat } from '../../hooks/use-real-time-chat';
import { getChatParticipants, getChatTitle } from '../../utils';
import { ParticipantAvatars } from '../avatar/ParticipantAvatars';
import { ChatBoxMessages } from './ChatBoxMessages';
import { ChatBoxInput } from './ChatBoxInput';
import { ChatBoxNewMessage } from './ChatBoxNewMessage';
import { ChatBoxSettings } from './settings/ChatBoxSettings';

/**
 * A chat box containing a single chat conversation.
 * - The chat box text size should remain the same regardless of window size,
 *   unlike the rest of the website, so it should use `px` instead of `rem`.
 */
export function ChatBox({
    className,
    user,
    conversationID,
    conversation: partialConversation,
}: {
    className?: string;
    /**
     * The current logged-in user.
     */
    user: MeUser;
    /**
     * The ID of the conversation. Set to `0` to create a new conversation.
     */
    conversationID: number;
    /**
     * The partial conversation object from the conversations list query. This
     * may be `undefined` if the conversation was not in the conversations list query,
     * e.g. was not in the paginated list.
     */
    conversation?: PartialConversation;
}) {
    const chatBoxId = useId();
    const [showSettings, setShowSettings] = useState(false);
    const [isFileAttachmentSent, setIsFileAttachmentSent] = useState(false);
    const [pollingIntervalMs, setPollingIntervalMs] = useState(0);
    const pageActive = useActivitySensor();
    const isNewConversation = conversationID <= 0;
    const {
        pendingMessage,
        isChatBoxOpen,
        openChatBox,
        minimizeChatBox,
        closeChatBox,
        setPendingMessage,
    } = useChat();
    const {
        conversation: fullConversation,
        messages,
        isSubmitting,
        isMarkAsReadBusy,
        refetch,
        createMessage,
        markAsRead,
    } = useConversation({
        id: conversationID,
        pollingIntervalMs,
    });
    const { isConnected } = useRealTimeChat({
        onMessage: (message) => {
            if (message.conversation_id === conversationID) {
                refetch();
            }
        },
    });
    const isOpen = useMemo(() => {
        return isChatBoxOpen(conversationID);
    }, [conversationID, isChatBoxOpen]);
    const conversation = useMemo(() => {
        return fullConversation ?? partialConversation;
    }, [fullConversation, partialConversation]);
    const isParticipantMember = useMemo(() => {
        return (
            !conversation ||
            conversation.Participants.some(
                ({ Identity }) =>
                    Identity.__typename === 'Member' && Identity.ID === user.ID
            )
        );
    }, [conversation, user.ID]);
    const unreadCount = useMemo(() => {
        return conversation
            ? conversation.UnreadMessageCount || Number(conversation.Unread)
            : 0;
    }, [conversation]);
    const otherParticipants = useMemo(() => {
        return getChatParticipants(conversation, user.ID);
    }, [conversation, user.ID]);
    const subtitle = useMemo(() => {
        if (otherParticipants.length === 1) {
            const otherParticipant = otherParticipants[0]!;
            return otherParticipant.__typename === 'Professional'
                ? otherParticipant.PrimaryCategory?.PrettyTitle
                : getUserOccupation(otherParticipant);
        }
        return '';
    }, [otherParticipants]);
    const isGroupChat =
        !isNewConversation &&
        conversation?.__typename === 'DirectConversation' &&
        conversation.IsGroupChat;

    const sendMessage = useCallback(
        (message: string, attachments: File[]) => {
            if (attachments?.length) {
                setIsFileAttachmentSent(true);
            } else {
                setIsFileAttachmentSent(false);
            }
            createMessage({ message, attachments });
        },
        [createMessage]
    );

    useEffect(() => {
        if (isConnected || !pageActive) {
            setPollingIntervalMs(300_000); // 5 minutes
        } else {
            // Fallback to polling if the WebSocket server is not connected.
            setPollingIntervalMs(30_000); // 30 seconds
        }
    }, [isConnected, pageActive]);

    useEffect(() => {
        // Mark the conversation as read if the chat box is open.
        if (
            isParticipantMember &&
            fullConversation &&
            (fullConversation.Unread || fullConversation.UnreadMessageCount) &&
            isOpen &&
            !isMarkAsReadBusy
        ) {
            markAsRead();
        }
    }, [
        isParticipantMember,
        fullConversation,
        isOpen,
        isMarkAsReadBusy,
        markAsRead,
    ]);

    useEffect(() => {
        // Automatically send the pending message when this chat box was
        // converted from the new message chat box.
        if (pendingMessage?.conversationID === conversationID) {
            sendMessage(pendingMessage.message, pendingMessage.attachments);
            setPendingMessage(null);
        }
    }, [conversationID, pendingMessage, sendMessage, setPendingMessage]);

    return (
        <div
            id={chatBoxId}
            className={cn(
                'flex flex-col overflow-hidden rounded-t-[16px] bg-white/50 backdrop-blur-xl transition-[height,width] duration-100 will-change-[height,width]',
                isOpen ? 'h-[640px]' : 'h-[48px]',
                className
            )}
            style={{
                width: isOpen
                    ? `${CHAT_BOX_OPEN_WIDTH_PX}px`
                    : `${CHAT_BOX_CLOSED_WIDTH_PX}px`,
            }}
            data-state={isOpen ? 'open' : 'closed'}
            aria-expanded={isOpen}
        >
            {/* Header */}
            <div
                className={cn(
                    'flex select-none items-center hover:bg-white/20',
                    isOpen ? 'gap-[12px] p-[12px]' : 'gap-[8px] p-[8px]'
                )}
                role="button"
                tabIndex={0}
                aria-label="Toggle conversation open"
                aria-controls={chatBoxId}
                onClick={() => {
                    if (isOpen) {
                        minimizeChatBox(conversationID);
                    } else {
                        openChatBox(conversationID);
                    }
                }}
            >
                {isNewConversation ? (
                    <div
                        className={cn(
                            'flex items-center justify-center rounded-full bg-white',
                            isOpen ? 'size-[48px]' : 'size-[32px]'
                        )}
                    >
                        <PencilIcon
                            className={cn(
                                'text-gray-600',
                                isOpen ? 'size-[24px]' : 'size-[16px]'
                            )}
                            strokeWidth="1.5px"
                        />
                    </div>
                ) : (
                    <ParticipantAvatars
                        className={cn(isOpen ? 'size-[40px]' : 'size-[32px]')}
                        fallbackClassName={cn(
                            isOpen ? '!text-[18px]' : '!text-[14px]'
                        )}
                        containerClassName="space-x-[-16px]"
                        participants={otherParticipants}
                        maxAvatars={isOpen ? 3 : 1}
                    />
                )}
                <div className="flex-1 space-y-[2px] truncate">
                    <div
                        className={cn(
                            'truncate text-[14px] text-foreground',
                            isOpen && 'font-medium'
                        )}
                    >
                        {isNewConversation
                            ? 'New Message'
                            : getChatTitle(conversation, otherParticipants)}
                    </div>
                    {isOpen && !isNewConversation && (
                        <div className="truncate text-[12px] text-foreground/80">
                            {subtitle}
                        </div>
                    )}
                </div>
                {unreadCount > 0 && (
                    <Badge
                        className="size-[18px] h-[18px] min-w-unset p-[4px] text-[11px]"
                        size="centeredCompact"
                        color="red"
                        contentOverflow="visible"
                    >
                        {unreadCount > 9 ? '9+' : unreadCount}
                    </Badge>
                )}
                {isOpen && isGroupChat && (
                    <button
                        className="flex items-center gap-[6px] rounded-lg border border-black/40 px-[8px] py-[4px] text-[12px] text-black/40 hover:bg-black/10"
                        onClick={(e) => {
                            e.stopPropagation();
                            setShowSettings((state) => !state);
                        }}
                    >
                        <UserRoundIcon className="size-[16px]" />
                        {conversation.Participants.length}
                    </button>
                )}
                <button
                    className={cn(
                        'rounded-full hover:bg-black/10',
                        isOpen ? 'p-[12px]' : 'p-[8px]'
                    )}
                    aria-label="Close"
                    onClick={(e) => {
                        e.stopPropagation();
                        closeChatBox(conversationID);
                    }}
                >
                    <XIcon className="size-[16px]" />
                </button>
            </div>
            {/* Chat messages and input */}
            <div
                className={cn(
                    'flex min-h-0 flex-1 flex-col gap-[8px] p-[8px] pb-[16px]',
                    !isOpen && 'hidden'
                )}
            >
                {isNewConversation ? (
                    <ChatBoxNewMessage
                        className="min-h-0 flex-1"
                        isOpen={isOpen}
                    />
                ) : showSettings && isGroupChat ? (
                    <ChatBoxSettings
                        className="min-h-0 flex-1"
                        conversation={conversation}
                        onClose={() => setShowSettings(false)}
                    />
                ) : (
                    <>
                        <Separator className="bg-white" />
                        <ChatBoxMessages
                            className="min-h-0 flex-1"
                            conversation={conversation}
                            messages={messages}
                            isLoading={!fullConversation}
                        />
                        <ChatBoxInput
                            isOpen={isOpen}
                            isDisabled={!fullConversation}
                            isNotParticipant={!isParticipantMember}
                            // Only show the submitting state when a message is sent
                            // WITH file attachments. This is prevent sending too quickly
                            // causing messages being sent in the wrong order.
                            isSubmitting={isSubmitting && isFileAttachmentSent}
                            onSend={({ message, attachments }) => {
                                sendMessage(message, attachments);
                            }}
                        />
                    </>
                )}
            </div>
        </div>
    );
}
